import { asDate } from "@@/shared/date-time/as-date";
import type { DateRange } from "@@/shared/date-time/date-range";
import { isWithinDateRange } from "@@/shared/date-time/date-range";
import { HttpError } from "@@/shared/errors/http-error";
import type { OptionalPercentage } from "@@/shared/optionals/optional-percentage";
import { isOptionalPercentage } from "@@/shared/optionals/optional-percentage";
import type { Price } from "@@/shared/prices/price";
import { zeroPrice } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { ProviderId } from "@@/shared/providers/provider-id";
import { reportIdFactory } from "@@/shared/reports/report-id";
import { support } from "@@/shared/support";
import type { TicketVoucher } from "@@/shared/tickets/ticket";
import { translation } from "@@/shared/translations/translation";
import { repeat } from "@@/utilities/repeat";

type VoucherReport = {
    readonly _id: string;
    readonly providerId: ProviderId;
    readonly range: DateRange;

    readonly dates: VoucherReportDay[];
};

type _helperDates = {
    readonly dates: {
        [date: string]: VoucherReportDay;
    };
};

type VoucherReportDay = {
    readonly _id: string;
    readonly dateStr: string;
    readonly date: Date;
    readonly activated: number;
    readonly expired: number;

    readonly activatedAmount: Price;
    readonly expiredAmount: Price;

    readonly activatedTickets: TicketVoucher[];
    readonly expiredTickets: TicketVoucher[];
};

const formatReportDateKey = (currentDate: Date) => {
    return `${currentDate.getFullYear()}${(currentDate.getMonth() + 1).toString().padStart(2, "0")}${currentDate.getDate().toString().padStart(2, "0")}`;
};

const emptyReportDay = (dateStr: string, date: Date): VoucherReportDay => ({
    _id: dateStr,
    dateStr: dateStr,
    date: date,
    activated: 0,
    expired: 0,
    activatedAmount: zeroPrice,
    expiredAmount: zeroPrice,
    activatedTickets: [],
    expiredTickets: [],
});

const voucherReportFactory = (params: {
    providerId: ProviderId;
    dateRange: DateRange;
    tickets: TicketVoucher[];
}) => {
    const { providerId, dateRange, tickets } = params;
    const report = tickets.reduce(
        (acc, ticket) => {
            const activatedDate = asDate(ticket.valid.start);
            const expiredDate = asDate(ticket.valid.end);

            const activatedFormattedDateKey =
                formatReportDateKey(activatedDate);
            const expiredFormattedDateKey = formatReportDateKey(expiredDate);

            let activatedDay =
                acc.dates[activatedFormattedDateKey] ??
                emptyReportDay(activatedFormattedDateKey, activatedDate);

            const ticketTotalPrice = sumPrices([
                ticket.price,
                ...ticket.optionals.map(o => {
                    const quantity = o.quantity.value;
                    if (o.optional._type === "OPTIONAL") {
                        return sumPrices(repeat(o.optional.price, quantity));
                    }
                    return zeroPrice;
                }),
            ]);

            const percentageOptionals = ticket.optionals.filter(o =>
                isOptionalPercentage(o),
            ) as OptionalPercentage[];
            if (percentageOptionals.length) {
                // if there are percentage optionals, we need to
                // calculate the price of the ticket with them
                // I'M NOT SURE IF THIS SHOULD BE POSSIBLE, AND
                // WHAT TO DO IF THERE ARE MANY OF THEM
                // eslint-disable-next-line no-console
                console.error(
                    `Percentage optionals found on voucher tickets when building a report for provider ${providerId}; ${percentageOptionals.map(o => o._id).join(", ")}. THIS IS NOT IMPLEMENTED YET`,
                );
                throw new HttpError(
                    500,
                    translation({
                        sv: `Vi stötte på ett problem när vi byggde rapporten, om felet kvarstår vänligen kontakta supporten på ${support.towni.supportEmail}`,
                        en: `We encountered a problem whilst building the report, if the error persists please contact support at ${support.towni.supportEmail}`,
                    }),
                );
            }

            activatedDay = {
                ...activatedDay,
                activated: activatedDay.activated + 1,

                activatedAmount: sumPrices([
                    activatedDay.activatedAmount,
                    ticketTotalPrice,
                ]),

                activatedTickets: [...activatedDay.expiredTickets, ticket],
            };
            let expiredDay =
                acc.dates[expiredFormattedDateKey] ??
                emptyReportDay(expiredFormattedDateKey, expiredDate);

            expiredDay = {
                ...expiredDay,
                expired: expiredDay.expired + 1,
                expiredAmount:
                    ticket.status !== "USED"
                        ? sumPrices([
                              expiredDay.expiredAmount,
                              ticketTotalPrice,
                          ])
                        : expiredDay.expiredAmount,
                expiredTickets: [...expiredDay.expiredTickets, ticket],
            };

            return {
                ...acc,
                dates: {
                    ...acc.dates,
                    [activatedFormattedDateKey]: activatedDay,
                    [expiredFormattedDateKey]: expiredDay,
                },
            };
        },
        {
            dates: {} as Record<string, VoucherReportDay>,
        } as _helperDates,
    );

    const days = Object.values(report.dates) satisfies VoucherReportDay[];

    return {
        _id: reportIdFactory(),
        providerId: providerId,
        range: dateRange,
        dates: days.filter(day => isWithinDateRange(day.date, dateRange)),
    } satisfies VoucherReport;
};

export { voucherReportFactory };
export type { VoucherReport, VoucherReportDay };
